import { useEffect, useRef, useState } from "react";
import { FaMapMarkerAlt } from "react-icons/fa";
import { hasMovedPastDragThreshold, pointerToNormalizedCoordinates } from "./aerialCoordinates";

const stopNativeTouch = (event) => {
	event.stopPropagation();
};

const AerialMarker = ({
	marker,
	field,
	imageReference,
	selected,
	expanded,
	scale,
	readOnly,
	onSelect,
	onConfirmSelect,
	onCancelSelect,
	onPreview,
	onSave,
	onPointerActiveChange,
}) => {
	const [dragging, setDragging] = useState(false);
	const markerReference = useRef(null);
	const pressStartReference = useRef(null);
	const draggingReference = useRef(false);

	useEffect(() => {
		const node = markerReference.current;
		if (!node) {
			return undefined;
		}

		// react-zoom-pan-pinch listens to native touch events on the wrapper.
		// React 17+ handlers run at the root after that, so capture-phase stop
		// is required to keep a pin drag from also panning the map.
		node.addEventListener("touchstart", stopNativeTouch, { capture: true });
		node.addEventListener("touchmove", stopNativeTouch, { capture: true });
		node.addEventListener("touchend", stopNativeTouch, { capture: true });
		node.addEventListener("touchcancel", stopNativeTouch, { capture: true });
		return () => {
			node.removeEventListener("touchstart", stopNativeTouch, { capture: true });
			node.removeEventListener("touchmove", stopNativeTouch, { capture: true });
			node.removeEventListener("touchend", stopNativeTouch, { capture: true });
			node.removeEventListener("touchcancel", stopNativeTouch, { capture: true });
		};
	}, []);

	const updateFromPointer = (pointerEvent) => {
		const coordinates = pointerToNormalizedCoordinates(pointerEvent.clientX, pointerEvent.clientY, imageReference.current);
		if (coordinates) {
			onPreview({ ...marker, ...coordinates });
		}
		return coordinates;
	};

	const releasePointer = (pointerEvent) => {
		if (pointerEvent.currentTarget.hasPointerCapture?.(pointerEvent.pointerId)) {
			pointerEvent.currentTarget.releasePointerCapture(pointerEvent.pointerId);
		}
	};

	const clearPress = () => {
		pressStartReference.current = null;
		draggingReference.current = false;
		setDragging(false);
		onPointerActiveChange?.(false);
	};

	const startPress = (pointerEvent) => {
		pointerEvent.preventDefault();
		pointerEvent.stopPropagation();
		onSelect(marker.fieldId);
		pressStartReference.current = {
			clientX: pointerEvent.clientX,
			clientY: pointerEvent.clientY,
			marker: { ...marker },
		};
		draggingReference.current = false;
		if (pointerEvent.currentTarget.setPointerCapture) {
			pointerEvent.currentTarget.setPointerCapture(pointerEvent.pointerId);
		}
		if (readOnly) {
			return;
		}
		onPointerActiveChange?.(true);
	};

	const movePress = (pointerEvent) => {
		if (!pressStartReference.current || readOnly) {
			return;
		}

		if (!draggingReference.current) {
			if (
				!hasMovedPastDragThreshold(
					pressStartReference.current.clientX,
					pressStartReference.current.clientY,
					pointerEvent.clientX,
					pointerEvent.clientY,
				)
			) {
				return;
			}
			draggingReference.current = true;
			setDragging(true);
		}

		pointerEvent.preventDefault();
		pointerEvent.stopPropagation();
		updateFromPointer(pointerEvent);
	};

	const finishPress = (pointerEvent) => {
		if (!pressStartReference.current) {
			return;
		}

		pointerEvent.preventDefault();
		pointerEvent.stopPropagation();
		const didDrag = draggingReference.current;
		if (didDrag && !readOnly) {
			const coordinates = updateFromPointer(pointerEvent);
			if (coordinates) {
				Promise.resolve(onSave({ ...marker, ...coordinates })).catch(() => {
					// The parent displays the save failure and retains the optimistic marker position.
				});
			}
			onCancelSelect?.();
		} else if (!didDrag) {
			onConfirmSelect?.(marker.fieldId);
		}
		releasePointer(pointerEvent);
		clearPress();
	};

	const cancelPress = (pointerEvent) => {
		if (pressStartReference.current?.marker && draggingReference.current) {
			onPreview(pressStartReference.current.marker);
		}
		onCancelSelect?.();
		releasePointer(pointerEvent);
		clearPress();
	};

	return (
		<button
			ref={markerReference}
			type="button"
			className={`aerial-marker absolute flex flex-col items-center touch-none ${
				selected ? "z-20 text-red-600" : "z-10 text-blue-600"
			} ${readOnly ? "cursor-pointer" : dragging ? "cursor-grabbing" : "cursor-grab"}`}
			style={{
				left: `${marker.relativeXCord * 100}%`,
				top: `${marker.relativeYCord * 100}%`,
				transform: `translate(-50%, -100%) scale(${1 / scale})`,
				transformOrigin: "50% 100%",
			}}
			title={field ? field.name : `Field ${marker.fieldId}`}
			aria-label={`Marker for ${field ? field.name : `field ${marker.fieldId}`}`}
			onPointerDown={startPress}
			onPointerMove={movePress}
			onPointerUp={finishPress}
			onPointerCancel={cancelPress}>
			{expanded && field ? (
				<span className="aerial-marker pointer-events-none mb-1 max-w-40 rounded bg-black/80 px-2 py-1 text-xs font-semibold text-white">
					{field.name}
				</span>
			) : null}
			<FaMapMarkerAlt className={`aerial-marker pointer-events-none text-3xl drop-shadow ${expanded ? "origin-bottom scale-125" : ""}`} />
		</button>
	);
};

export default AerialMarker;
